import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';

import H2 from '../H2';
import { H3 } from '../H3/H3';
import { P } from '../P/P';
import Stacheldraht from '../Stacheldraht';
import { ExternalLink } from '../ExternalLink/ExternalLink';

import { mediaPhone, minTablet, minDesktop } from '../styles/breakpoints';
import { HeadlineFont, Headline2ndFont } from '../styles/fonts';
import { TextColor } from '../styles/colors';

import ShowMoreIconImg from './icons/show-more-plus.svg';

const UL = styled(P)`
  margin-left: 1em;
`;

const StacheldrahtStyled = styled(Stacheldraht)`
  margin-top: 40px;

  ${minDesktop} {
    display: none;
  }
`;

const Article = styled.article`
  @media (min-width: 540px) {
    margin-top: 35px;
  }
  ${minTablet} {
    margin-bottom: 30px;
  }
  @media (min-width: 950px) {
    margin-top: 35px;
  }
  ${minDesktop} {
    margin-top: 55px;
  }
`;

const ExtendedSection = styled.section`
  display: ${({ show }) => (show ? 'block' : 'none')};
  margin-top: 10px;
`;

const ResetButtonStyles = css`
  -webkit-appearance: none;
  border: none;
  background-color: transparent;

  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }
`;

const ShowMoreButton = styled.button([
  ResetButtonStyles,
  HeadlineFont,
  `
  font-size: 22px;
  ${mediaPhone} {
    font-size: 18px;
  }

  color: ${TextColor};

  display: block;
  margin: 50px auto 20px;
  padding-left: 36px;
  line-height: 24px;

  background-size: 24px 24px;
  background-repeat: no-repeat;
  background-position: left;
  background-image: url(${ShowMoreIconImg});

  text-transform: uppercase;
`]);

const H2Styled = styled(H2)`
  ${Headline2ndFont};
  margin-top: 30px;
  margin-bottom: 10px;
`;

class Introduction extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showLongContent: false,
    };
  }

  showLongContent() {
    this.setState({ showLongContent: true });
  }

  render() {
    const { className } = this.props;
    const { showLongContent } = this.state;

    return (
      <Article className={className}>
        <section>

          <P gapAfter>Liebe Lehrerinnen und Lehrer,</P>

          <P gapAfter>
            modebewusst sein bedeutet laut Duden: &#8222;<b>sich bewusst nach der Mode richtend</b>”. Frühjahr, Sommer, Herbst und Winter – für jede Saison locken Hersteller und Modezeitungen mit neuer trendiger Kleidung – Jahr für Jahr. Große Modeketten bringen sogar bis zu acht Kollektionen jedes Jahr auf den Markt. Wer modisch up to date bleiben will, kauft sich also kontinuierlich neue Kleidung – jeder Deutsche im Durchschnitt 60 Teile im Jahr.
            <br />
            Zwar <ExternalLink href="https://www.zukunftsinstitut.de/artikel/das-ende-der-fast-fashion-aera/">sieht das Zukunftsinstitut langfristig den Trend zu nachhaltiger und fairer Mode</ExternalLink> und auch große Modehäuser wie H&M bieten mittlerweile nachhaltige Mode an. Aber noch konsumieren viele Fast Fashion und die entsprechenden Retailer verzeichnen hohe Umsätze.
            <br />
            In den letzten Jahren gab es zahlreiche kritische Medienberichte und viele Aktionen von gemeinnützigen Organisationen, die auf die Problematik der schnellen Wegwerf-Mode aufmerksam gemacht haben. Auch gibt es mittlerweile viele alternative Handlungsmöglichkeiten – gerade über neue Medien, wie z. B. Apps zum Tauschen, Verkaufen und Leihen sowie YouTube-Tutorials zum Selbermachen und Upcycling.
            <br />
            Dennoch scheinen die vermeintlichen Vorteile von Fast Fashion aus persönlicher Sicht bei vielen Verbrauchern noch zu überwiegen, die negativen Wirkungen werden verdrängt oder aber die Alternativen sind zu wenig bekannt oder zu undurchsichtig, wie z. B. faire Siegel: Was ist da wirklich fair und was nicht mehr als eine Goodwill-Kampagne oder sogar Greenwashing?
            <br />
            Daher möchten wir auf dieser Website Lehrkräften der Sekundarstufe 1 Impulse geben, wie sie das Thema im Unterricht bearbeiten können – und zwar ohne den erhobenen Zeigefinger, denn die Jugendlichen sollen selbst ihr Verhalten reflektieren. Ziel ist, dass die Jugendlichen <b>bewusst mit Mode umgehen</b>.
            <br />
            Wir verweisen an passender Stelle auf die Materialien anderer Herausgeber, die sich gut einbinden lassen, und bieten eine kommentierte Link-Liste, sodass sich ohne aufwendige Eigenrecherche die richtigen Materialien für den Unterricht finden lassen.
            <br />
            In der Smartboard-Galerie auf dieser Seite finden Sie – neben den Arbeitsblättern und Grafiken – Stimmungsbilder, um das Unterrichtsthema visuell zu untermalen. Wir möchten an dieser Stelle der Postcode-Lotterie danken, mit deren Förderung wir dieses Projekt an den Start bringen konnten.
            <br />
            Ihnen wünschen wir einen erlebnisreichen Unterricht zum Thema Mode<em>bewusst</em>.
          </P>

          <P>Ihr Bund für Bildung e. V.</P>

          { showLongContent || (
            <ShowMoreButton onClick={() => this.showLongContent()}>Mehr anzeigen</ShowMoreButton>
          )}
        </section>
        <ExtendedSection show={showLongContent}>

          <H2Styled>Lernziele:</H2Styled>
          <UL>
            <li>Wissen vermitteln über Warenströme der Modeindustrie und deren wirtschaftliche, soziale und ökonomische Auswirkungen</li>
            <li>Reflexion über (auf Mode bezogene) Werte</li>
            <li>Reflexion über Handlungen als Mode-Konsument</li>
            <li>Kritischer Umgang mit persönlichen Einstellungen und äußeren Einflüssen</li>
            <li>Kennenlernen von Handlungsoptionen</li>
            <li>Fähigkeit zum Handeln als mündiger, reflektierter und verantwortungsvoller Konsument und Weltbürger</li>
          </UL>

          <H2Styled>ACHTUNG:</H2Styled>
          <P>
            Bitte beachten Sie, dass Mode auch ein sensibles und heikles Thema sein kann. Es gibt zum einen Schülerinnen und Schüler (SuS), die sich Mode nicht leisten können und daher vielleicht ausgegrenzt werden, und zum anderen SuS, die sich stark über Mode identifizieren. Hier sollte man keine individuellen Auswertungen in den Mittelpunkt des Unterrichts stellen, sondern Auswertungen (wie z. B. die Kleiderschrank-Analyse) im Klassenverband verallgemeinern, indem man auf konkrete Zahlen verzichtet. Generell ist es sehr wichtig, keine Wertung vorzunehmen, sondern zur Reflexion anzuregen.
          </P>

          <H2Styled>Hintergrund:</H2Styled>
          <P>
            Jugendliche wählen nach einer Greenpeace-Studie von 2015 ihre Kleidung hauptsächlich nach dem Aussehen und dem Preis aus, das Herstellungsland oder Zertifizierungen (z. B. bio, Fair Trade) spielen hingegen kaum eine Rolle. Nur 10 bis 20 Prozent der Jugendlichen und jungen Erwachsenen zwischen 16 und 25 Jahren zeigten sich in einer weiteren Studie zum Thema (&#8222;Jugendliche und nachhaltiger Konsum”, 2017) dazu bereit, auf Produkte zu verzichten, wenn sie nicht umweltfreundlich oder unter fairen Arbeitsbedingungen produziert würden. Dabei interessieren sich weit mehr als die Hälfte der befragten Jugendlichen für Themen wie Umwelt- oder Klimaschutz. Haltung und Konsumverhalten stehen demnach deutlich im Gegensatz zueinander: Dies zeigt den Bedarf an Information über die Auswirkungen ihres aktuellen Modekonsums und über erschwingliche, attraktive und nachhaltige Mode.
          </P>

          <H2Styled>Didaktische Grundlagen unserer Unterrichtsimpulse:</H2Styled>

          <H3>Bildung für nachhaltige Entwicklung (BNE)</H3>
          <P>
            &#8222;Gemeint ist eine Bildung, die Menschen zu zukunftsfähigem Denken und Handeln befähigt: Wie beeinflussen meine Entscheidungen Menschen nachfolgender Generationen oder in anderen Erdteilen? Welche Auswirkungen hat es beispielsweise, wie ich konsumiere …? (...) Bildung für nachhaltige Entwicklung ermöglicht es jedem Einzelnen, die Auswirkungen des eigenen Handelns auf die Welt zu verstehen und verantwortungsvolle Entscheidungen zu treffen.”
            <br />
            (<ExternalLink href="https://www.bne-portal.de" />)
          </P>

          <H3>Global Citizenship Education</H3>
          <P>
            Unsere Impulse sind problem- und schülerorientiert gestaltet. Es geht uns darum, dass die SuS das Wissen selbst erwerben, kritisch reflektieren und die nachhaltige Entwicklung im eigenen Umfeld mitgestalten. Im Sinne der Global Citizenship Education erfahren die SuS Verantwortung und Selbstwirksamkeit.
          </P>

          <H2Styled>Lehrplananbindung:</H2Styled>
          <P>
            Mode an sich ist nicht Gegenstand der Lehrpläne für die Sekundarstufe 1. Allerdings lassen sich daran verschiedene Themenkomplexe einzeln oder aufeinander bezogen fächerspezifisch, fachübergreifend und fächerverbindend behandeln: Identität, Vorbilder, Abgrenzung und Zugehörigkeit; digitale Welt und Medienbildung; ökologische, soziale, kulturelle und ökonomische Nachhaltigkeit, Konsum und Verzicht; Produktionskreisläufe, Wirtschaft und Industrie, Globalisierung. Alle diese Themenkomplexe sind Gegenstand von Lehr- und Kompetenzplänen. Für die SuS werden sie über Mode als Teil ihrer Lebenswelt verstehbar.
          </P>

        </ExtendedSection>
        <StacheldrahtStyled small />
      </Article>
    );
  }
}

Introduction.propTypes = {
  className: PropTypes.string,
};

Introduction.defaultProps = {
  className: undefined,
};

export default Introduction;



// WEBPACK FOOTER //
// ./src/components/AboutMaterialDownload/Introduction.js